// Player-facing notice modal — styled overlay for "something
// expected went wrong" messages: failed downloads, offline state,
// rate limits, server errors during normal play, etc.
//
// Use this instead of the native `alert()` for any condition that
// could reasonably happen during a normal play session. `alert()`
// renders as a browser debug dialog (unstyled, looks like a developer
// error) and is reserved for genuinely unexpected exceptions —
// invariant violations that shouldn't happen under reasonable
// circumstances. See CLAUDE.md "Player-facing notifications".
//
// Layout / styling reuses the existing `.auth-overlay` + `.auth-modal`
// stack from the auth gate so the site styling is consistent without
// adding new CSS.

export interface NoticeOptions {
  /** Heading row at the top of the modal. Defaults to "Notice". */
  title?: string;
  /** Body text. */
  message: string;
  /** Label for the dismiss button. Defaults to "OK". */
  buttonLabel?: string;
  /** Called once the modal is dismissed (either via the button or
   *  by tapping outside the modal box). */
  onDismissed?: () => void;
}

export function showNotice(options: NoticeOptions): void {
  const overlay = document.createElement('div');
  overlay.className = 'auth-overlay notice-overlay';
  overlay.innerHTML = `
    <div class="auth-modal notice-modal">
      <h2>${escapeHtml(options.title ?? 'Notice')}</h2>
      <p class="account-gate-message">${escapeHtml(options.message)}</p>
      <div class="auth-buttons">
        <button class="menu-btn primary" id="notice-ok">${escapeHtml(options.buttonLabel ?? 'OK')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    overlay.remove();
    options.onDismissed?.();
  };

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('#notice-ok')!.addEventListener('click', close);
}

export interface ConfirmOptions {
  /** Heading row. Defaults to "Confirm". */
  title?: string;
  /** Body text describing what the user is about to do. */
  message: string;
  /** Label for the affirmative button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** When true, the confirm button uses the destructive (red) style.
   *  Use for delete / discard / sign-out flows so the visual weight
   *  matches the consequence. */
  danger?: boolean;
  /** Override the confirm button's color variant. Takes an existing
   *  `.menu-btn` modifier class (e.g. `'primary-blue'`, `'primary-green'`)
   *  to match other gem/coin cost buttons elsewhere in the UI. Ignored
   *  when `danger: true` wins. */
  confirmVariant?: 'primary' | 'primary-blue' | 'primary-green' | 'primary-purple';
  /** Fires when the user picks the confirm button. */
  onConfirm: () => void;
  /** Fires when the user cancels (either via the cancel button OR by
   *  tapping outside the modal). Optional — many callers only care
   *  about confirmation. */
  onCancel?: () => void;
}

/** Styled confirm dialog. Used in place of `window.confirm()` for any
 *  action that needs a yes/no acknowledgement (delete deck, sign out,
 *  etc.). Visual stack matches `showNotice` so the site looks coherent
 *  regardless of which kind of modal pops. */
export function showConfirm(options: ConfirmOptions): void {
  const overlay = document.createElement('div');
  overlay.className = 'auth-overlay notice-overlay';
  const confirmClass = options.danger
    ? 'menu-btn danger'
    : `menu-btn ${options.confirmVariant ?? 'primary'}`;
  overlay.innerHTML = `
    <div class="auth-modal notice-modal">
      <h2>${escapeHtml(options.title ?? 'Confirm')}</h2>
      <p class="account-gate-message">${escapeHtml(options.message)}</p>
      <div class="auth-buttons">
        <button class="menu-btn" id="confirm-cancel">${escapeHtml(options.cancelLabel ?? 'Cancel')}</button>
        <button class="${confirmClass}" id="confirm-ok">${escapeHtml(options.confirmLabel ?? 'Confirm')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  let closed = false;
  const close = (confirmed: boolean): void => {
    if (closed) return;
    closed = true;
    overlay.remove();
    if (confirmed) options.onConfirm();
    else options.onCancel?.();
  };

  overlay.addEventListener('click', (e) => {
    // Tap-outside dismisses as cancel — same affordance as showNotice
    // for muscle memory.
    if (e.target === overlay) close(false);
  });
  overlay.querySelector('#confirm-cancel')!.addEventListener('click', () => close(false));
  overlay.querySelector('#confirm-ok')!.addEventListener('click', () => close(true));
}

export interface PromptOptions {
  /** Heading row. Defaults to "Enter a value". */
  title?: string;
  /** Body text describing what the input is for. Optional — many
   *  cases are clear enough from the title alone. */
  message?: string;
  /** Initial input value. Pre-selects so the player can immediately
   *  type a replacement. */
  defaultValue?: string;
  /** Placeholder shown when the input is empty. */
  placeholder?: string;
  /** Label for the affirmative button. Defaults to "OK". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Max length of the input. Default 80. */
  maxLength?: number;
  /** Receives the (trimmed) value when the player confirms. Not
   *  called on cancel or on empty input — the dialog stays open
   *  until the player either submits a non-empty value or cancels. */
  onConfirm: (value: string) => void;
  onCancel?: () => void;
}

/** Styled text-input dialog. Drop-in replacement for `window.prompt()`
 *  so the look matches the rest of the site. Enter submits, Escape
 *  cancels, tap-outside cancels. Empty input is rejected silently
 *  (the input gets focus and the dialog stays put). */
export function showPrompt(options: PromptOptions): void {
  const overlay = document.createElement('div');
  overlay.className = 'auth-overlay notice-overlay';
  overlay.innerHTML = `
    <div class="auth-modal notice-modal">
      <h2>${escapeHtml(options.title ?? 'Enter a value')}</h2>
      ${options.message ? `<p class="account-gate-message">${escapeHtml(options.message)}</p>` : ''}
      <input type="text" id="prompt-input"
             class="prompt-input"
             value="${escapeHtml(options.defaultValue ?? '')}"
             placeholder="${escapeHtml(options.placeholder ?? '')}"
             maxlength="${options.maxLength ?? 80}" />
      <div class="auth-buttons">
        <button class="menu-btn" id="prompt-cancel">${escapeHtml(options.cancelLabel ?? 'Cancel')}</button>
        <button class="menu-btn primary" id="prompt-ok">${escapeHtml(options.confirmLabel ?? 'OK')}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const input = overlay.querySelector('#prompt-input') as HTMLInputElement;
  // Pre-select the default so a fresh typed value replaces it cleanly.
  // setTimeout to defer until after the overlay's appended to DOM so
  // focus / select actually take effect on iOS Safari.
  setTimeout(() => {
    input.focus();
    input.select();
  }, 0);

  let closed = false;
  const close = (value: string | null): void => {
    if (closed) return;
    closed = true;
    overlay.remove();
    if (value !== null) options.onConfirm(value);
    else options.onCancel?.();
  };

  const submit = (): void => {
    const trimmed = input.value.trim();
    if (!trimmed) {
      input.focus();
      return;
    }
    close(trimmed);
  };

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close(null);
  });
  overlay.querySelector('#prompt-cancel')!.addEventListener('click', () => close(null));
  overlay.querySelector('#prompt-ok')!.addEventListener('click', submit);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close(null);
    }
  });
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
